import { PaginationState } from 'app/shared/pagination/pagination.model';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showMailMergeValidationErrors, showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { toast } from 'react-toastify';
import { ERRORS, messages } from 'app/config/constants';
import { FileUploadModel } from 'app/shared/file-upload/model/file-upload.model';
import {
  deleteCaseActAttachment,
  fetchCaseActAttachments,
  generateEarhivaConfirmation,
  previewAmendmentCaseActWorkingVersionFileWithDefaultTemplate,
  previewCaseActWorkingVersionFileWithDefaultTemplate,
  uploadActFile,
} from 'app/edozvola/cases/case-acts/service/case-act.service';
import {
  changeCaseActActivity,
  checkAmendmentCaseActAndTemplateExists,
  checkCaseActAndTemplateExists,
  fetchCaseAct,
  fetchCaseActs,
  fetchPreviewCaseActs,
  saveAmendmentCaseActWithDefaultTemplate,
  saveCaseAct,
  saveCaseActWithDefaultTemplate,
  searchPublishedCaseActs,
} from '../../service/cases.service';
import { CaseActFormModel } from 'app/edozvola/cases/cases.model';
import { openFileInNewTab } from 'app/shared/util/file-utils';
import success = toast.success;
import error = toast.error;
import { openFileInNewWindow } from 'app/shared/util/navigation-util';
import { CaseActRequestDescriptionModel } from 'app/edozvola/cases/case-acts/model/case-acts.model';

export const fetchCaseActsQuery = (pagination: PaginationState, uuid: string) => {
  return useQuery({
    queryKey: ['case-acts-table', { ...pagination }, uuid],
    queryFn: ({ signal }) => fetchCaseActs({ signal, pagination, uuid }),
  });
};

export const fetchPreviewCaseActsQuery = (pagination: PaginationState, uuid: string) => {
  return useQuery({
    queryKey: ['case-acts-table', { ...pagination }, uuid],
    queryFn: ({ signal }) => fetchPreviewCaseActs({ signal, pagination, uuid }),
  });
};

export const searchPublishedCaseActsQuery = (pagination: PaginationState, searchParam: string) => {
  return useQuery({
    queryKey: ['case-acts-table', { ...pagination }, searchParam],
    queryFn: ({ signal }) => searchPublishedCaseActs({ signal, pagination, searchParam }),
  });
};

export const fetchCaseActQuery = (uuid: string, idCaseAct: string) => {
  return useQuery({
    queryKey: ['case-act', uuid, idCaseAct],
    queryFn: () => fetchCaseAct({ uuid, idCaseAct }),
    enabled: !!idCaseAct,
  });
};

export const updateCaseActQuery = (uuid: string, resetForm: Function, successCallback: Function) => {
  return useMutation({
    mutationFn: (data: CaseActFormModel) => saveCaseAct(uuid, data),
    onSuccess: (data: CaseActFormModel) => {
      showToast(success, data.idCaseAct ? messages.DATA_SAVED : 'Akt je uspješno izrađen.');
      queryClient.invalidateQueries({ queryKey: ['case-acts-table'] });
      resetForm({ values: data });
      successCallback();
    },
    onError: (err: any) => {
      showToast(error, ERRORS.ERROR_ACT_GENERATION);
    },
  });
};

export const updateCaseActWithDefaultTemplateQuery = (uuid: string, successCallback: Function) => {
  return useMutation({
    mutationFn: (caseActRequestDescription: string) => saveCaseActWithDefaultTemplate(uuid, caseActRequestDescription),
    onSuccess: (data: CaseActFormModel) => {
      showToast(success, 'Dokument zahtjeva je uspješno izrađen.');
      queryClient.invalidateQueries({ queryKey: ['case-acts-table'] });
      successCallback(data);
    },
    onError: (err: any) => {
      if (
        err.response.data.parameters &&
        err.response.data.parameters.mandatoryMailMergeFieldsThatAreEmpty &&
        err.response.data.parameters.mandatoryMailMergeFieldsThatAreEmpty.length > 0
      ) {
        showMailMergeValidationErrors(err.response.data.parameters.mandatoryMailMergeFieldsThatAreEmpty);
      } else {
        showToast(error, ERRORS.ERROR_ACT_GENERATION);
      }
    },
  });
};

export const updateAmendmentCaseActWithDefaultTemplateQuery = (uuid: string, successCallback: Function) => {
  return useMutation({
    mutationFn: (caseActRequestDescription: string) => saveAmendmentCaseActWithDefaultTemplate(uuid, caseActRequestDescription),
    onSuccess: (data: CaseActFormModel) => {
      showToast(success, 'Dokument dopune zahtjeva je uspješno izrađen.');
      queryClient.invalidateQueries({ queryKey: ['case-acts-table'] });
      successCallback(data);
    },
    onError: (err: any) => {
      if (
        err.response.data.parameters &&
        err.response.data.parameters.mandatoryMailMergeFieldsThatAreEmpty &&
        err.response.data.parameters.mandatoryMailMergeFieldsThatAreEmpty.length > 0
      ) {
        showMailMergeValidationErrors(err.response.data.parameters.mandatoryMailMergeFieldsThatAreEmpty);
      } else {
        showToast(error, ERRORS.ERROR_ACT_GENERATION);
      }
    },
  });
};

export const previewCaseActWorkingVersionFileWithDefaultTemplateQuery = (uuid: string) => {
  return useMutation({
    mutationFn: () => previewCaseActWorkingVersionFileWithDefaultTemplate(uuid),
    onSuccess: (data, variables, context) => {
      if (data) {
        openFileInNewWindow(data.url)
      }
    },
    onError: err => {
      showToast(error, 'Dogodila se greška prilikom pregleda dokumenta.');
    },
  });
};

export const previewAmendmentCaseActWorkingVersionFileWithDefaultTemplateQuery = (uuid: string) => {
  return useMutation({
    mutationFn: () => previewAmendmentCaseActWorkingVersionFileWithDefaultTemplate(uuid),
    onSuccess: (data, variables, context) => {
      if (data) {
        openFileInNewWindow(data.url);
      }
    },
    onError: err => {
      showToast(error, 'Dogodila se greška prilikom pregleda dokumenta.');
    },
  });
};

export const checkCaseActAndTemplateExistsQuery = (uuid: string) => {
  return useQuery({
    queryKey: ['case-act-and-template-exists', uuid],
    queryFn: () => checkCaseActAndTemplateExists({ uuid }),
  });
};

export const checkAmendmentCaseActAndTemplateExistsQuery = (uuid: string) => {
  return useQuery({
    queryKey: ['amendment-case-act-and-template-exists', uuid],
    queryFn: () => checkAmendmentCaseActAndTemplateExists({ uuid }),
  });
};

export const changeActActivityQuery = (uuid: string) => {
  return useMutation({
    mutationFn: (idCaseAct: number) => changeCaseActActivity(uuid, idCaseAct),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-acts-table'] });
    },
  });
};

export const uploadActAttachmentQuery = (idCaseAct: string, onSuccessCallback, onErrorCallback) => {
  return useMutation({
    mutationFn: (file: FileUploadModel) => uploadActFile(idCaseAct, file),
    onSuccess: (data, variables, context) => onSuccessCallback(variables),
    onError: (data, variables, context) => onErrorCallback(variables),
  });
};

export const fetchCaseActAttachmentsQuery = (pagination: PaginationState, idCaseAct: string) => {
  return useQuery({
    queryKey: ['case-act-attachments-table', { idCaseAct: idCaseAct, pagination: { ...pagination } }],
    queryFn: ({ signal }) =>
      fetchCaseActAttachments(idCaseAct, pagination.page, pagination.size, pagination.column, pagination.direction),
    enabled: idCaseAct !== null,
  });
};

export const deleteCaseActAttachmentQuery = () => {
  return useMutation({
    mutationFn: (idProjectDocumentAttachment: number) => deleteCaseActAttachment(idProjectDocumentAttachment),
    onSuccess: () => {
      showToast(success, messages.ATTACHMENT_DELETED);
      queryClient.invalidateQueries({ queryKey: ['case-act-attachments-table'] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const generateEarhivaConfirmationQuery = (caseUuid: string) => {
  return useMutation({
    mutationFn: () => generateEarhivaConfirmation(caseUuid),
    onSuccess: (data: any) => {
      if (data) {
        const applicationType = data.fileType ? `${data.fileType.type}/${data.fileType.subtype}` : 'application/pdf';
        openFileInNewTab(data.fileName, data.contentData, applicationType);
      }
    },
    onError: (err: any) => {
      const emptyMandatoryFields = err?.response?.data?.parameters?.mandatoryMailMergeFieldsThatAreEmpty;
      if (emptyMandatoryFields && emptyMandatoryFields.length > 0) {
        showMailMergeValidationErrors(emptyMandatoryFields);
        return;
      }
      showToast(error, err?.response?.data?.detail ?? 'Dogodila se greška prilikom izrade potvrde o evidentiranim podacima.');
    },
  });
};
